// Iter 64 — Copy Summary: plain-text rendering of a compare turn's structured metadata
// (object_context.compare_context). Never parses the AI answer; never claims equivalence,
// replacement suitability or runtime behaviour.

const SCOPE_NOTE =
  "Scope: workspace-scoped comparison of recorded metadata only — not a statement of equivalence, replacement suitability, or runtime behavior.";

function sideName(s) {
  if (!s || typeof s !== "object") return null;
  if (s.qualified) return String(s.qualified);
  if (!s.name) return null;
  return s.library ? `${s.library}/${s.name}` : String(s.name);
}

function kindWord(s) {
  const k = s && (s.object_type || s.kind);
  return k ? ` (${String(k).replace(/-/g, " ")})` : "";
}

function count(n) {
  return n === null || n === undefined || Number.isNaN(Number(n)) ? null : Number(n);
}

/** Text for one list: names (+N more) from details, count-only fallback, "None recorded." or null when not applicable. */
export function listText(list, fallbackCount) {
  if (list && typeof list === "object" && Array.isArray(list.names)) {
    const more = count(list.more) || 0;
    if (!list.names.length && !more) return "None recorded.";
    const names = list.names.map(String).join(", ");
    return more > 0 ? `${names}${names ? " " : ""}(+${more} more)` : names;
  }
  const c = count(fallbackCount);
  if (c === null) return null; // not applicable to this pair
  return c === 0 ? "None recorded." : `${c} (names not available)`;
}

function formatStamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function stateText(state, stale) {
  if (!state) return "none recorded";
  return `${state} disposition${stale ? " (stale)" : ""}`;
}

/** Plain text for engagement notes, or null when the message carries no compare metadata. */
export function buildCompareSummaryText(oc, opts = {}) {
  const cc = oc && typeof oc === "object" && oc.compare_context && typeof oc.compare_context === "object" ? oc.compare_context : null;
  const s = cc && cc.used && cc.summary && typeof cc.summary === "object" ? cc.summary : null;
  if (!s) return null;
  const left = sideName(cc.left);
  const right = sideName(cc.right);
  if (!left || !right) return null;
  const det = cc.details && typeof cc.details === "object" ? cc.details : {};
  const dRules = det.rules || {};
  const dDeps = det.dependencies || {};
  const dFields = det.fields || {};
  const dScreens = det.screens || {};
  const dEv = det.evidence || {};
  const rules = s.rules || {};
  const deps = s.dependencies || {};
  const fields = s.fields || {};
  const screens = s.screens || {};
  const ev = s.evidence || {};
  const disp = s.dispositions || {};
  const w = s.warnings || {};
  const refLabel = cap(deps.referencing_label || "callers");
  const filesLabel = cap(deps.files_label || "files used");

  const lines = [`Comparison: ${left} vs ${right}`, `Left: ${left}${kindWord(cc.left)}`, `Right: ${right}${kindWord(cc.right)}`];
  const stamp = formatStamp(opts.generatedAt);
  if (stamp) lines.push(`Generated: ${stamp}`);
  lines.push(SCOPE_NOTE, "");

  const group = (title, rows) => {
    lines.push(`${title}:`);
    const shown = rows.filter(([, text]) => text !== null);
    if (!shown.length) lines.push("- None recorded.");
    shown.forEach(([label, text]) => lines.push(`- ${label}: ${text}`));
    lines.push("");
  };
  group("Shared", [
    ["Rules", listText(dRules.shared_names, rules.shared)],
    [refLabel, listText(dDeps.shared_callers, deps.shared_callers)],
    ["Called programs", listText(dDeps.shared_called, deps.shared_called)],
    [filesLabel, listText(dDeps.shared_files, deps.shared_files)],
    ["Fields", listText(dFields.shared_names, fields.shared)],
    ["Screen formats", listText(dScreens.shared_formats, screens.shared_formats)],
  ]);
  group("Left only", [
    ["Rules", listText(dRules.left_only_names, rules.left_only)],
    [refLabel, listText(dDeps.left_only_callers, deps.left_only_callers)],
    ["Called programs", listText(dDeps.left_only_called, deps.left_only_called)],
    [filesLabel, listText(dDeps.left_only_files, deps.left_only_files)],
    ["Fields", listText(dFields.left_only_names, fields.left_only)],
    ["Screen formats (all on this side)", listText(dScreens.left_formats, screens.left_count)],
  ]);
  group("Right only", [
    ["Rules", listText(dRules.right_only_names, rules.right_only)],
    [refLabel, listText(dDeps.right_only_callers, deps.right_only_callers)],
    ["Called programs", listText(dDeps.right_only_called, deps.right_only_called)],
    [filesLabel, listText(dDeps.right_only_files, deps.right_only_files)],
    ["Fields", listText(dFields.right_only_names, fields.right_only)],
    ["Screen formats (all on this side)", listText(dScreens.right_formats, screens.right_count)],
  ]);

  lines.push("Dispositions:", `- Left: ${stateText(disp.left_state, disp.left_stale)}`, `- Right: ${stateText(disp.right_state, disp.right_stale)}`, "");

  const warnings = [];
  if (disp.left_stale) warnings.push("Left object has a stale proposed disposition — re-check before citing it.");
  if (disp.right_stale) warnings.push("Right object has a stale proposed disposition — re-check before citing it.");
  const ambiguous = count(fields.ambiguous) || 0;
  if (ambiguous > 0) {
    const names = listText(dFields.ambiguous_names, null);
    warnings.push(`${ambiguous} field reference(s) carry an ambiguous mapping${names && names !== "None recorded." ? `: ${names}` : ""} — confirm before relying on them.`);
  }
  const unresolved = count(w.unresolved_screens) || 0;
  if (unresolved > 0) warnings.push(`${unresolved} screen step(s) are unresolved (display file not proven).`);
  lines.push("Warnings:");
  (warnings.length ? warnings : ["None recorded."]).forEach((t) => lines.push(`- ${t}`));
  lines.push("");

  lines.push("Evidence:");
  const labels = dEv.batch_labels && Array.isArray(dEv.batch_labels.names) ? dEv.batch_labels : null;
  if (labels && labels.names.length) {
    labels.names.forEach((n) => lines.push(`- ${n}`));
    if ((count(labels.more) || 0) > 0) lines.push(`- +${labels.more} more batch(es)`);
  } else if ((count(ev.batch_count) || 0) > 0) {
    lines.push(`- ${ev.batch_count} import batch(es) (labels not available)`);
  }
  const unknown = count(dEv.unknown_provenance_count ?? ev.unknown_provenance_count) || 0;
  if (unknown > 0) lines.push(`- Unknown provenance: ${unknown} supporting row(s) carry no batch id.`);
  if ((!labels || !labels.names.length) && !(count(ev.batch_count) > 0) && !unknown) lines.push("- None recorded.");

  // Iter 66 — impact graph delta (omitted entirely for messages that predate it)
  const gd = cc.graph_delta && typeof cc.graph_delta === "object" ? cc.graph_delta : null;
  if (gd) {
    lines.push("", `Impact overlap (impact graph, depth ${count(gd.depth) ?? 2}, bounded):`);
    const gwarn = Array.isArray(gd.warnings) ? gd.warnings.map(String) : [];
    if (!gd.used || !gd.shared || !gd.left_only || !gd.right_only) {
      lines.push(`- Not available: ${gwarn.length ? gwarn.join(" ") : "impact graph delta not recorded."}`);
    } else {
      const gl = gd.left || {};
      const gr = gd.right || {};
      const ln = count(gl.node_count) ?? 0;
      const rn = count(gr.node_count) ?? 0;
      lines.push(`- Left graph: ${ln} nodes, ${count(gl.edge_count) ?? 0} edges`, `- Right graph: ${rn} nodes, ${count(gr.edge_count) ?? 0} edges`);
      const larger = gd.larger_side === "equal" ? `equal (${ln} nodes each)` : gd.larger_side === "left" ? `left (${ln} vs ${rn} nodes)` : gd.larger_side === "right" ? `right (${rn} vs ${ln} nodes)` : null;
      if (larger) lines.push(`- Larger blast radius by node count: ${larger} (size alone is not risk).`);
      const cats = (g) => Object.entries(g.edge_categories || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "none";
      [["Shared", gd.shared], ["Left only", gd.left_only], ["Right only", gd.right_only]].forEach(([label, g]) => {
        lines.push(`- ${label} blast radius (${count(g.node_count) ?? 0} nodes, ${count(g.edge_count) ?? 0} edges): ${listText(g.nodes, g.node_count)}`);
        lines.push(`  - Direct callers: ${listText(g.callers, null) ?? "None recorded."}`);
        lines.push(`  - Directly referenced: ${listText(g.referenced, null) ?? "None recorded."}`);
        lines.push(`  - Fields: ${listText(g.fields, g.field_count) ?? "None recorded."}`);
        lines.push(`  - Edge types: ${cats(g)}`);
      });
      gwarn.forEach((w) => lines.push(`- Warning: ${w}`));
    }
  }

  return lines.join("\n").trimEnd();
}

/** navigator.clipboard first, legacy execCommand fallback; resolves true on success. */
export async function copyToClipboard(text) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    if (typeof document === "undefined" || typeof document.execCommand !== "function") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}

/** { ok, text } — text is null when the message is not a compare turn. */
export async function copyCompareSummary(oc, opts = {}) {
  const text = buildCompareSummaryText(oc, opts);
  if (!text) return { ok: false, text: null };
  const ok = await copyToClipboard(text);
  return { ok, text };
}

export const COPY_SUCCESS_MESSAGE = "Copied comparison summary.";
export const COPY_FAILURE_MESSAGE = "Copy failed — your browser blocked clipboard access. Select the panel text and copy it manually.";
